import {
  Checkbox,
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import Button, {
  ButtonDesigns,
  ButtonTypes,
} from 'src/components/Button/Button'
import Popup from 'src/components/Popup/Popup'
import SvgOrganizationBuildingIcon from 'src/components/Svgs/SvgOrganizationBuildingIcon'
import { Acknowledgements } from 'src/enums'
import { useTranslate } from 'src/localization/Trns'
import { useSendOrgRolesAcknowledgement } from 'src/queries/organizations/acknowledgements/acknowledgements.queries'
import { selectAppName } from 'src/redux/selectors/session.selectors'
import styled, { css } from 'styled-components'

interface OrgRoleAcknowledgementsPopupProps {
  onClose: () => void
}

const StyledDiv = styled.div`
  border-radius: 10px;
  background-color: ${styleConstants.colors.gray_light};
  display: flex;
  padding: 20px;
  flex-direction: column;
  flex: 1 0 0;
  gap: 12px;
  margin-top: 10px;
`

const OrgRoleAcknowledgementsPopup: React.FC<
  OrgRoleAcknowledgementsPopupProps
> = (props): React.ReactElement => {
  const translate = useTranslate()

  const appName = useSelector(selectAppName)
  const [acknowledgedRoles, setAcknowledgedRoles] = useState<boolean>(false)
  const {
    mutate: postSendOrgRolesAcknowledgement,
    isPending: postSendOrgRolesAcknowledgementLoading,
    error: postSendOrgRolesAcknowledgementError,
  } = useSendOrgRolesAcknowledgement()

  return (
    <Popup
      onClose={props.onClose}
      css={css`
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 10px;
        width: 700px;
        padding: var(--spacing-base-unit-15, 60px);
      `}
    >
      <div
        css={css`
          height: 153.037px;
          position: absolute;
          top: 54px;
          right: 10px;
          border-top-right-radius: 10px;
          overflow: hidden;
        `}
      >
        <SvgOrganizationBuildingIcon />
      </div>
      <span
        css={css`
          ${getFontCss(
            16,
            styleConstants.colors.red_dark,
            styleConstants.fontFamilies.regular,
          )}
        `}
      >
        {translate('Action Required')}
      </span>
      <span
        css={css`
          ${getFontCss(
            32,
            themeConstants.colors.font_primary,
            styleConstants.fontFamilies.medium,
          )}
        `}
      >
        {translate('Acknowledge role permission changes')}
      </span>
      <span
        css={css`
          ${getFontCss(
            16,
            themeConstants.colors.font_primary,
            styleConstants.fontFamilies.regular,
          )}
          margin-top: 30px;
        `}
      >
        {translate(
          "Please acknowledge the following upcoming changes to ${brandName}'s roles and the potential impact on your current and future card accounts.",
          {
            brandName: appName,
          },
        )}
      </span>
      <StyledDiv>
        <span
          css={css`
            ${getFontCss(
              16,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.medium,
            )}
          `}
        >
          {translate('I acknowledge that...')}
        </span>
        <span
          css={css`
            ${getFontCss(
              16,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.regular,
            )}
          `}
        >
          {translate(
            'The Owner, any current or future Admin(s), and any Card Manager given access to my card account(s) at the discretion of the Owner will have the following edit access with respect to my current and future registered card accounts:',
          )}
        </span>
        <ul
          css={css`
            list-style-position: inside;
          `}
        >
          <li>
            <span
              css={css`
                ${getFontCss(
                  16,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.regular,
                )}
              `}
            >
              {translate('They can ${bold} virtual cards and budgets', {
                bold: (
                  <span
                    css={css`
                      ${getFontCss(
                        16,
                        themeConstants.colors.font_primary,
                        styleConstants.fontFamilies.medium,
                      )}
                    `}
                  >
                    {translate('create')}
                  </span>
                ),
              })}
            </span>
          </li>
          <li>
            <span
              css={css`
                ${getFontCss(
                  16,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.regular,
                )}
              `}
            >
              {translate('They can ${bold} virtual cards and budgets', {
                bold: (
                  <span
                    css={css`
                      ${getFontCss(
                        16,
                        themeConstants.colors.font_primary,
                        styleConstants.fontFamilies.medium,
                      )}
                    `}
                  >
                    {translate('close')}
                  </span>
                ),
              })}
            </span>
          </li>
          <li>
            <span
              css={css`
                ${getFontCss(
                  16,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.regular,
                )}
              `}
            >
              {translate('They can ${bold} virtual card and budget details', {
                bold: (
                  <span
                    css={css`
                      ${getFontCss(
                        16,
                        themeConstants.colors.font_primary,
                        styleConstants.fontFamilies.medium,
                      )}
                    `}
                  >
                    {translate('update')}
                  </span>
                ),
              })}
            </span>
          </li>
        </ul>
        <span
          css={css`
            ${getFontCss(
              16,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.regular,
            )}
          `}
        >
          {translate(
            "All actions will be logged in the 'History' of each virtual card or budget, including details of the user who performed the action.",
          )}
        </span>
      </StyledDiv>
      <div
        css={css`
          display: flex;
          flex-direction: row;
          margin-top: 10px;
        `}
      >
        <Checkbox
          isSelected={acknowledgedRoles}
          onClick={(): void => setAcknowledgedRoles(!acknowledgedRoles)}
        />
        <span
          css={css`
            ${getFontCss(
              16,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.medium,
            )}
          `}
        >
          {translate('I acknowledge these changes')}
        </span>
      </div>
      <Button
        css={css`
          margin-top: 30px;
        `}
        id={'completeRegButton'}
        onClick={(): void =>
          postSendOrgRolesAcknowledgement({
            type: Acknowledgements.OwnerAdminCardManagerMigration,
          })
        }
        type={ButtonTypes.button}
        design={ButtonDesigns.designCta}
        text={translate('Acknowledge')}
        loading={postSendOrgRolesAcknowledgementLoading}
        disabled={
          !acknowledgedRoles || !!postSendOrgRolesAcknowledgementLoading
        }
        big
      />
      {postSendOrgRolesAcknowledgementError &&
        postSendOrgRolesAcknowledgementError.details &&
        postSendOrgRolesAcknowledgementError.details.length > 0 && (
          <>
            {postSendOrgRolesAcknowledgementError.details.map((error) => (
              <span
                css={css`
                  ${getFontCss(
                    16,
                    styleConstants.colors.red_dark,
                    styleConstants.fontFamilies.regular,
                  )}
                `}
                key={error.error}
              >
                {error.error}
              </span>
            ))}
          </>
        )}
    </Popup>
  )
}

export default OrgRoleAcknowledgementsPopup
